import {
  isReportSortField,
  useFetchHomeDomains,
} from '@http/queries/useFetchHomeDomains';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type PropsWithChildren,
  type Ref,
} from 'react';
import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import HomeReportTable from './HomeReportTable';
import TableHeading from '@/components/Tables/TableHeading';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';

const DEFAULT_PAGE_SIZE = 10;

/** Typing should not fire a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Up to this many domains, the overview is fetched whole and the table searches,
 * sorts, filters and pages it in the browser — the only way the report-derived
 * columns (score, average error count, page count) can be ordered at all, since
 * the API orders by stored columns only. Past it, the API pages the set and
 * those three columns lose their sort control and their filter field.
 */
export const MAX_ENTRIES_FOR_CLIENT_SIDE_TABLE = 290;

/**
 * Every filter value arrives as `unknown`, and `Array.isArray` narrows that to
 * `any[]` — which would spread `any` through everything read out of it.
 */
function toArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? (value as unknown[]) : undefined;
}

/** The multiselect holds the picked levels; empty means it was cleared. */
function toLevels(value: unknown): string[] | undefined {
  const levels = toArray(value);
  if (!levels?.length) return undefined;

  return levels.map(String);
}

/**
 * The target filter, in the shape the API takes it. It is the only column filter
 * the paged table offers, so nothing else in the filter state becomes a
 * parameter.
 */
function toConformanceTargetLevels(
  columnFilters: ColumnFiltersState,
): string[] | undefined {
  return toLevels(
    columnFilters.find((filter) => filter.id === 'conformanceTarget')?.value,
  );
}

const FIRST_PAGE: PaginationState = {
  pageIndex: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

const DEFAULT_SORTING: SortingState = [{ id: 'domain', desc: false }];

/** Same object when already on page one, so React bails out of a no-op reset. */
const toFirstPage = (current: PaginationState): PaginationState =>
  current.pageIndex === 0 ? current : { ...current, pageIndex: 0 };

/** Use the API total before downloading the full client-side set. */
export default function DomainOverviewSection() {
  const { data: response } = useFetchHomeDomains({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    search: '',
    pinnedOnly: false,
    sortBy: 'domain',
    sortDirection: 'asc',
  });

  return response.total <= MAX_ENTRIES_FOR_CLIENT_SIDE_TABLE ? (
    <ClientSideDomainOverview />
  ) : (
    <PagedDomainOverview />
  );
}

function OverviewSection({
  sectionRef,
  children,
}: PropsWithChildren<{ sectionRef?: Ref<HTMLElement> }>) {
  const { t } = useInlineTranslation();

  return (
    <section
      id='homeReportTableSection'
      ref={sectionRef}
      aria-label={t('overviewOfAllDomains')}
      // The app header is fixed, so scrolling here has to clear its height.
      className='eye:scroll-mt-[var(--app-header-height)]'
    >
      <TableHeading title={<T k='overviewOfAllDomains' />} />
      {children}
    </section>
  );
}

/**
 * One request for the whole set; from then on every search, sort, filter and
 * page change is a re-render, not a round trip.
 */
function ClientSideDomainOverview() {
  const [pinnedOnly, setPinnedOnly] = useState(false);

  const { data: response } = useFetchHomeDomains({
    limit: MAX_ENTRIES_FOR_CLIENT_SIDE_TABLE,
  });

  // The total may grow between the initial page and the full-set request.
  if (response.total > response.data.length) return <PagedDomainOverview />;

  const rows = pinnedOnly
    ? response.data.filter((row) => row.isPinned)
    : response.data;

  return (
    <OverviewSection>
      <HomeReportTable
        data={rows}
        pinnedOnly={pinnedOnly}
        onPinnedOnlyChange={setPinnedOnly}
      />
    </OverviewSection>
  );
}

/**
 * Search, sorting, the target filter, pin filtering and paging all run on the
 * API, so each covers every domain rather than the page on screen.
 *
 * Every change suspends, so all of them go through `startTransition` — otherwise
 * the section drops to its Suspense fallback instead of dimming the rows.
 */
function PagedDomainOverview() {
  const sectionRef = useRef<HTMLElement>(null);

  const [pagination, setPagination] = useState<PaginationState>(FIRST_PAGE);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pinnedOnly, setPinnedOnly] = useState(false);
  // The box updates on every keystroke; only the settled value reaches the query.
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [isRefiningResults, startTransition] = useTransition();

  const [sortColumn] = sorting;
  const sortBy =
    sortColumn && isReportSortField(sortColumn.id) ? sortColumn.id : undefined;

  const conformanceTargetLevels = useMemo(
    () => toConformanceTargetLevels(columnFilters),
    [columnFilters],
  );

  const { data: response } = useFetchHomeDomains({
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    search: appliedSearch,
    pinnedOnly,
    conformanceTargetLevels,
    sortBy,
    sortDirection: sortBy && sortColumn.desc ? 'desc' : 'asc',
  });

  /**
   * Applies the typed term once it settles. The commit must be inside the
   * transition — it changes the query key, and an urgent suspending update would
   * swap the table for the section's fallback rather than dim it.
   */
  useEffect(() => {
    if (search === appliedSearch) return;

    const timeout = setTimeout(() => {
      startTransition(() => {
        setAppliedSearch(search);
        setPagination(toFirstPage);
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [search, appliedSearch]);

  /** Changing which rows match returns to page one, which may no longer exist. */
  const refineResults = (apply: () => void) =>
    startTransition(() => {
      apply();
      setPagination(toFirstPage);
    });

  /**
   * The page controls sit below the rows, so turning the page leaves the reader
   * scrolled to where the previous page ended — that is the middle of the new
   * one. Put the top of the table back in view instead.
   *
   * Not inside the transition: the scroll should happen on the click, not once
   * the next page has loaded.
   */
  const goToPage = (nextPagination: PaginationState) => {
    const isPageChange = nextPagination.pageIndex !== pagination.pageIndex;

    startTransition(() => setPagination(nextPagination));

    if (isPageChange) {
      sectionRef.current?.scrollIntoView({ block: 'start' });
    }
  };

  return (
    <OverviewSection sectionRef={sectionRef}>
      <HomeReportTable
        data={response.data}
        canUseReportValues={false}
        busy={isRefiningResults}
        manualPagination={{
          pagination,
          onPaginationChange: goToPage,
          rowCount: response.total,
        }}
        manualFiltering={{
          search,
          // Keystrokes only move the input; the debounce above commits it.
          onSearchChange: setSearch,
          columnFilters,
          onColumnFiltersChange: (nextColumnFilters) =>
            refineResults(() => setColumnFilters(nextColumnFilters)),
        }}
        manualSorting={{
          sorting,
          onSortingChange: (nextSorting) =>
            refineResults(() => setSorting(nextSorting)),
        }}
        pinnedOnly={pinnedOnly}
        onPinnedOnlyChange={(next) => refineResults(() => setPinnedOnly(next))}
      />
    </OverviewSection>
  );
}
